import "@/components/Interface/FormFields/FormField/FormField.scss";
import "./Combobox.scss";

import { Combobox as HeadlessCombobox, Transition } from "@headlessui/react";
import clsx from "clsx";
import { debounce } from "lodash";
import {
  forwardRef,
  Fragment,
  ReactElement,
  Ref,
  RefObject,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { ControllerRenderProps } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ChevronDown } from "@/assets/icons/ds";
import { foreground, surface } from "@/styles/colors";

/** The open trigger flattens the corners on the side the options attach to. */
const openTriggerRadius = {
  down: {
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  up: {
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },
} as const;

const optionsPosition = {
  down: { top: "100%", bottom: "auto", borderRadius: "0 0 8px 8px" },
  up: { top: "auto", bottom: "100%", borderRadius: "8px 8px 0 0" },
} as const;

interface ComboboxProps<OptionType> {
  className?: string;
  displayValue?: (item: any) => string;
  options: readonly OptionType[];
  placeholder: string;
  label?: string;
  dataTestId: string;
  disabled?: boolean;
  /** When true, appends "(optional)" to the label. Required fields have no indicator. */
  optional?: boolean;
  hasError?: boolean;
  field: ControllerRenderProps<any, any>;
  /**
   * `"stacked"` = FormField's portal edit density (label static above the
   * value, 64 px-ish row) - same trigger as FormField's own `layout` prop:
   * pass this explicitly, or wrap the form in `.dire-form-fields--stacked`
   * and every Combobox inside picks it up the same way FormField does.
   * Default `"floating"` = the standard floating-label trigger.
   */
  layout?: "floating" | "stacked";
  /** Same as FormField's `variant`: "dark" for fields on a dark surface. */
  variant?: "light" | "dark";
}
export const Combobox = <OptionType,>({
  className,
  options,
  label,
  displayValue = (item: string) => item,
  disabled,
  dataTestId,
  optional = false,
  hasError,
  field,
  layout = "floating",
  variant = "light",
}: ComboboxProps<OptionType>) => {
  const stacked = layout === "stacked";
  const [query, setQuery] = useState("");
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const labelWithOptional =
    label && optional && !disabled
      ? `${label} ${t("translation:Common.label.optional")}`
      : (label ?? "");

  // Filter options based on input
  const filteredOptions =
    query === ""
      ? options
      : options.filter((option) =>
          displayValue(option)
            .toLowerCase()
            .replace(/\s+/g, "")
            .includes(query.toLowerCase().replace(/\s+/g, "")),
        );

  // Once open, the trigger itself turns into the same dark surface as
  // the flyout (not just a light trigger with a panel below it) — the two
  // form one seamless dark block. Lifted up here (rather than living inside
  // ComboboxOptionsList) because the trigger's own rounding needs to know
  // it too: flat on the side the flyout attaches to, rounded on the other.
  //
  // Driven via inline styles rather than a toggled class — Chrome has
  // repeatedly failed to repaint the trigger when only a class changed on
  // an already-mounted node (confirmed the computed style matches but not
  // the actual paint), and toggling the whole `style` attribute between an
  // object and `undefined` had the same symptom. The style object below is
  // always rendered on both the trigger and its wrapper (some page-local
  // styles, e.g. PersonalDataForm's stacked country field, flatten the
  // trigger to `display: contents` so the wrapper owns the visible box
  // instead) - only the property values flip, which repaints reliably.
  const [openUpward, setOpenUpward] = useState(false);
  const openStyle = { backgroundColor: surface.primary, borderRadius: 0 };
  const openTextStyle = { color: foreground["primary-inverse"] };

  return (
    <HeadlessCombobox
      value={field.value}
      onChange={field.onChange}
      disabled={disabled}
      // react-hook-form clones defaultValues, so `field.value` is never the
      // same object reference as its match in `options` - Headless UI's
      // default `===` comparison would then never mark anything selected.
      // `displayValue` already uniquely characterizes an option for the UI,
      // so it doubles as a reference-independent equality check here.
      by={(a, b) => displayValue(a) === displayValue(b)}
    >
      {({ open }) => {
        const triggerBoxStyle = {
          backgroundColor: open ? openStyle.backgroundColor : undefined,
          ...(open && openTriggerRadius[openUpward ? "up" : "down"]),
        };

        return (
          <div
            className={clsx(
              "relative",
              // Stacked density's box lives on this same element (mirrors
              // FormField's own `.form-field-container` - see the shared
              // rule in FormField.scss). `dire-select-stacked` is this
              // combobox's own generic hook for the parts that rule can't
              // know about (the chevron button, the flyout's anchor point).
              stacked && [
                "form-field-container form-field-container--stacked dire-select-stacked",
                hasError && "invalid",
                disabled && "is-disabled",
              ],
              className,
            )}
            ref={wrapperRef}
            style={triggerBoxStyle}
          >
            <div
              className={clsx(
                "dire-input dire-select",
                variant === "dark" && "dire-input--dark",
                hasError && "invalid",
                disabled && "is-disabled",
              )}
              style={triggerBoxStyle}
            >
              <HeadlessCombobox.Input
                id={field.name}
                data-testid={dataTestId}
                // register the input with react-hook-form
                className="dire-input__control"
                name={field.name}
                onChange={(event) => setQuery(event.target.value)}
                placeholder=" "
                displayValue={displayValue}
                style={{ color: open ? openTextStyle.color : undefined }}
              />
              {label && (
                <label
                  className="dire-input__label"
                  htmlFor={field.name}
                  style={{ color: open ? openTextStyle.color : undefined }}
                >
                  {labelWithOptional}
                </label>
              )}
              <HeadlessCombobox.Button
                data-testid={`${dataTestId}-button`}
                className="dire-select__button"
                aria-label={t("common.buttons.showOptions")}
              >
                <ChevronDown
                  className="dire-select__icon"
                  aria-hidden="true"
                  style={{ color: open ? openTextStyle.color : undefined }}
                />
              </HeadlessCombobox.Button>
            </div>
            <Transition
              as={Fragment}
              enter="transition duration-100 ease-out"
              enterFrom="transform scale-95 opacity-0"
              enterTo="transform scale-100 opacity-100"
              leave="transition duration-75 ease-out"
              leaveFrom="transform scale-100 opacity-100"
              leaveTo="transform scale-95 opacity-0"
              afterLeave={() => setQuery("")}
            >
              <ComboboxOptionsList
                open={open}
                wrapperRef={wrapperRef}
                filteredOptions={filteredOptions}
                query={query}
                displayValue={displayValue}
                t={t}
                onOpenUpwardChange={setOpenUpward}
              />
            </Transition>
          </div>
        );
      }}
    </HeadlessCombobox>
  );
};

/**
 * Headless UI 1.x has no anchored-positioning support, so the options list
 * always opened downward and got clipped when the field sat near the
 * bottom of the viewport. Measure available space the moment the list
 * opens — regardless of what triggered it (button click, input click while
 * already focused, or keyboard) — and flip it upward when there isn't
 * enough room below.
 *
 * Wrapped in forwardRef because Transition's `as={Fragment}` clones this
 * element and attaches its own ref directly to it.
 */
interface ComboboxOptionsListProps<OptionType> {
  open: boolean;
  wrapperRef: RefObject<HTMLDivElement | null>;
  filteredOptions: readonly OptionType[];
  query: string;
  displayValue: (item: OptionType) => string;
  t: (key: string) => string;
  // `Transition as={Fragment}` clones this element and injects its own
  // enter/leave classes via this prop — without accepting and merging it,
  // the fade/scale animation silently never reaches the DOM node.
  className?: string;
  onOpenUpwardChange: (openUpward: boolean) => void;
}

// forwardRef doesn't support generics directly, so the generic type param is
// declared on the inner render function and the whole thing is cast back to
// a generic component — this keeps ComboboxOptionsList<OptionType> in sync
// with the OptionType established by the outer Combobox<OptionType>.
const ComboboxOptionsList = forwardRef(function ComboboxOptionsList<OptionType>(
  {
    open,
    wrapperRef,
    filteredOptions,
    query,
    displayValue,
    t,
    className,
    onOpenUpwardChange,
  }: ComboboxOptionsListProps<OptionType>,
  ref: Ref<HTMLUListElement>,
) {
  const [openUpward, setOpenUpward] = useState(false);

  useLayoutEffect(() => {
    if (!open) {
      return;
    }

    const updateOpenDirection = () => {
      const rect = wrapperRef.current?.getBoundingClientRect();
      if (!rect) {
        return;
      }
      const OPTIONS_MAX_HEIGHT = 288; // matches max-h-72
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const nextOpenUpward =
        spaceBelow < OPTIONS_MAX_HEIGHT && spaceAbove > spaceBelow;
      setOpenUpward(nextOpenUpward);
      onOpenUpwardChange(nextOpenUpward);
    };

    updateOpenDirection();

    // On mobile, focusing the input (which opens the combobox) also brings
    // up the on-screen keyboard, which shrinks window.innerHeight *after*
    // this measurement already ran. Re-measure whenever the viewport
    // actually changes size while the list stays open. Debounced
    // (matching useIsMobile's pattern) since resize fires on every tick
    // of the on-screen-keyboard show/hide animation.
    const debouncedUpdate = debounce(updateOpenDirection, 100);
    window.addEventListener("resize", debouncedUpdate);
    window.visualViewport?.addEventListener("resize", debouncedUpdate);

    return () => {
      window.removeEventListener("resize", debouncedUpdate);
      window.visualViewport?.removeEventListener("resize", debouncedUpdate);
      debouncedUpdate.cancel();
    };
  }, [open, wrapperRef, onOpenUpwardChange]);

  return (
    <HeadlessCombobox.Options
      className={clsx("dire-select__options", className)}
      modal={false}
      ref={ref}
      // Class-toggled position/rounding has repeatedly failed to repaint
      // reliably on this node (see the trigger's dark-state comment above) —
      // set directly so the browser has no choice but to reflect it.
      style={optionsPosition[openUpward ? "up" : "down"]}
    >
      {filteredOptions.length === 0 && query !== "" ? (
        // No result for query / input. An <li> option, not a div: the
        // listbox <ul> may only hold options. Disabled, so it can't be picked.
        <li
          data-testid="nothingFound"
          className="noResult"
          role="option"
          aria-disabled="true"
          aria-selected="false"
        >
          {t("Common.nothingFound")}
        </li>
      ) : (
        filteredOptions.map((option, i: number) => (
          <HeadlessCombobox.Option
            key={i}
            className={({ active }) => `option ${active ? "active" : ""}`}
            value={option}
          >
            {({ selected }) => (
              <>
                {selected && <span className="signifier" aria-hidden="true" />}
                <span className="truncate-safe block">
                  {displayValue(option)}
                </span>
              </>
            )}
          </HeadlessCombobox.Option>
        ))
      )}
    </HeadlessCombobox.Options>
  );
}) as <OptionType>(
  props: ComboboxOptionsListProps<OptionType> & {
    ref?: Ref<HTMLUListElement>;
  },
) => ReactElement;
